import React, { useEffect, useState } from 'react';
import type { LinkExit } from 'react-native-plaid-link-sdk';
import { FullScreenSpinner, isWeb, YStack } from 'smoothie';

import { IS_PRODUCTION, IS_STAGING } from '../../../config/FruitfulEnvironment';
import { getWebUrl } from '../../../lib/Web';
import { captureException, captureMessage } from '../../../logger/Logger';
import type { User } from '../../../user/types/User';
import useCreateExternalAccount from '../../hooks/plaid/useCreateExternalAccount';
import useCreateExternalAccountNew from '../../hooks/plaid/useCreateExternalAccountNew';
import useCreateLinkToken from '../../hooks/plaid/useCreateLinkToken';
import useCreatePermanentAccessToken from '../../hooks/plaid/useCreatePermanentAccessToken';
import type { PlaidSuccessListenerArgs } from '../../types/Plaid';
import PlaidLink from './PlaidLink';

// Universal links for this redirect are only set up in staging and production.
// The page is the static file public/oauth/plaid.html. It has to stay a real
// file so the bank redirect is not rewritten to the web app.
const PLAID_REDIRECT_URI =
    IS_STAGING || IS_PRODUCTION ? `${getWebUrl()}/oauth/plaid.html` : undefined;

interface PlaidProps {
    children: React.ReactNode;
    onSuccessfulLink?: (needsReview?: boolean) => void;
    onExitLink?: () => void;
    onNeedsReview: () => void;
    user: User;
}

const Plaid = ({
    children,
    onSuccessfulLink,
    onExitLink,
    onNeedsReview,
    user,
}: PlaidProps) => {
    const [linkToken, setLinkToken] = useState<string | null>(null);
    const [linkSource, setLinkSource] = useState<string | null>(null);

    const {
        mutateAsync: createLinkToken,
        isPending: isLoadingCreateLinkToken,
    } = useCreateLinkToken();
    const {
        mutateAsync: createPermanentAccessToken,
        isPending: isLoadingCreatePermanentAccessToken,
    } = useCreatePermanentAccessToken();
    const {
        mutateAsync: createExternalAccount,
        isPending: isLoadingCreateExternalAccount,
    } = useCreateExternalAccount();

    const {
        mutateAsync: createExternalAccountNew,
        isPending: isLoadingCreateExternalAccountNew,
    } = useCreateExternalAccountNew();

    useEffect(() => {
        if (user.externalAccountLinkingEnabled === true) {
            createLinkToken({
                body: {
                    stytchId: user.stytchId,
                    ...(!isWeb && { redirectUri: PLAID_REDIRECT_URI }),
                },
            })
                .then(({ linkToken, linkSource }) => {
                    setLinkToken(linkToken);
                    setLinkSource(linkSource);
                })
                .catch((e) => {
                    captureException(e);
                });
        }
    }, [user, setLinkToken]);

    const onSuccess = async ({
        publicToken,
        metadata,
    }: PlaidSuccessListenerArgs): Promise<void> => {
        captureMessage(`Metadata: ${JSON.stringify(metadata)}`);

        const { stytchId } = user;

        // Get institution id from metadata.
        let institutionId: string | null = null;
        const institution = metadata.institution ?? {};
        if ('institution_id' in institution) {
            institutionId = institution.institution_id as string;
        } else if ('id' in institution) {
            institutionId = institution.id as string;
        }

        if (institutionId == null) {
            return;
        }

        if (linkSource == null) {
            throw new Error('Expected linkSource to be populated');
        }

        try {
            if (linkSource === 'Synctera') {
                captureMessage('Starting create with linkSource === Synctera');
                const { vendor_access_token: vendorAccessToken } =
                    await createPermanentAccessToken({
                        body: {
                            stytchId,
                            vendorInstitutionId: institutionId,
                            vendorPublicToken: publicToken,
                        },
                    });

                await createExternalAccount({
                    body: {
                        stytchId,
                        institutionId,
                        vendorAccessToken,
                        vendorAccountIds: metadata.accounts.map(
                            ({ id }) => id as string,
                        ),
                    },
                });
                onSuccessfulLink?.();
            } else if (linkSource === 'Fruitful') {
                captureMessage('Starting create with linkSource === Fruitful');
                const result = await createExternalAccountNew({
                    body: {
                        stytchId,
                        publicToken,
                    },
                });
                // Passing the result into onSuccessfulLink is only for the
                // first funding screen, which automatically navigates to the
                // next screen after linking.
                onSuccessfulLink?.(result.review === true);
                if (result.review === true) {
                    onNeedsReview();
                }
            } else {
                throw new Error(`Unexpected link source ${linkSource}`);
            }
        } catch (e) {
            captureException(e);
        } finally {
            onExitLink?.();
        }
    };

    const onExit = (response: LinkExit) => {
        captureMessage(`Exited plaid linking: ${JSON.stringify(response)}`);
        onExitLink?.();
    };

    if (
        isLoadingCreateLinkToken ||
        isLoadingCreatePermanentAccessToken ||
        isLoadingCreateExternalAccount ||
        isLoadingCreateExternalAccountNew
    ) {
        return <FullScreenSpinner />;
    }

    return (
        <YStack>
            <PlaidLink
                linkToken={linkToken}
                onSuccess={onSuccess}
                onExit={onExit}
            >
                {children}
            </PlaidLink>
        </YStack>
    );
};

export default Plaid;
